import { useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Download, Upload } from "lucide-react";
import { APK_MAX_BYTES, type LatestAppReleaseResponse } from "@mubin/contracts";
import { ErrorNote, Field, PageTitle } from "../components/ui";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import { AndroidTestersCard } from "../components/android-testers";

export const formatSize = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} م.ب`;

/**
 * Upload the Android app (owner request 2026-10-21, like Sulam Al-Wusool): the APK goes to the server, and
 * the public link /download always serves the newest. Installed apps offer it when it is a newer native build.
 */
export function AppReleasePage() {
  const org = useOrg();
  const qc = useQueryClient();
  const latest = useQuery({
    queryKey: ["app-release"],
    queryFn: () => api<LatestAppReleaseResponse>("/v1/app-releases/android/latest"),
  });
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [versionName, setVersionName] = useState("");
  const [notes, setNotes] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/download`;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!org || !file) return;
    if (!file.name.toLowerCase().endsWith(".apk")) return setError("اختر ملف تطبيق أندرويد بامتداد ‎.apk");
    if (file.size > APK_MAX_BYTES) return setError("حجم الملف أكبر من 200 م.ب");
    setError(null);
    setProgress(0);
    const params = new URLSearchParams({ organizationId: org.id, versionName: versionName.trim() });
    if (notes.trim()) params.set("notes", notes.trim());
    // XMLHttpRequest, not fetch: it reports upload progress.
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `/v1/app-releases/android?${params}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("content-type", "application/vnd.android.package-archive");
    xhr.upload.onprogress = (ev) =>
      ev.lengthComputable && setProgress(Math.round((ev.loaded / ev.total) * 100));
    xhr.onload = () => {
      setProgress(null);
      if (xhr.status === 201) {
        setFile(null);
        setVersionName("");
        setNotes("");
        if (fileRef.current) fileRef.current.value = "";
        void qc.invalidateQueries({ queryKey: ["app-release"] });
      } else {
        try {
          setError((JSON.parse(xhr.responseText) as { error: { message: string } }).error.message);
        } catch {
          setError("تعذر رفع الملف");
        }
      }
    };
    xhr.onerror = () => {
      setProgress(null);
      setError("انقطع الاتصال أثناء الرفع، حاول مرة أخرى");
    };
    xhr.send(file);
  }

  const r = latest.data?.release;
  return (
    <>
      <PageTitle
        title="تطبيق أندرويد"
        sub="ارفع ملف التطبيق (APK)، فيصبح متاحًا للتحميل من الرابط المباشر، وتظهر للمستخدمين رسالة بوجود إصدار جديد."
        actions={
          <button
            className="btn btn-secondary"
            onClick={() => void navigator.clipboard?.writeText(link).then(() => setCopied(true))}
          >
            <Copy size={16} strokeWidth={1.75} /> {copied ? "تم نسخ الرابط" : "نسخ رابط التحميل"}
          </button>
        }
      />
      <section className="card">
        <div className="card-h">
          <h2>الإصدار الحالي</h2>
        </div>
        <div className="section">
          {r ? (
            <dl className="kv" style={{ padding: 0 }}>
              <dt>الإصدار</dt>
              <dd className="ltr">{r.versionName}</dd>
              <dt>الحجم</dt>
              <dd>{formatSize(r.size)}</dd>
              <dt>تاريخ الرفع</dt>
              <dd>{new Date(r.createdAt).toLocaleString("ar-SA")}</dd>
              {r.notes && (
                <>
                  <dt>ما الجديد</dt>
                  <dd>{r.notes}</dd>
                </>
              )}
              <dt>رابط التحميل</dt>
              <dd>
                <a className="ltr" href={link} target="_blank" rel="noreferrer">
                  {link}
                </a>
              </dd>
            </dl>
          ) : (
            <p className="muted">لم يُرفع أي إصدار بعد.</p>
          )}
          {r && !r.runtimeVersion && (
            <p className="notice small">
              لم نتمكن من قراءة نسخة التشغيل من الملف، فلن تظهر للمستخدمين رسالة التحديث لهذا الإصدار.
            </p>
          )}
          {r && (
            <a className="btn btn-secondary btn-sm" href={r.downloadPath} style={{ marginTop: 12 }}>
              <Download size={16} strokeWidth={1.75} /> تحميل الملف
            </a>
          )}
        </div>
      </section>
      <section className="card">
        <div className="card-h">
          <h2>رفع إصدار جديد</h2>
        </div>
        <form className="section form" onSubmit={submit}>
          <ErrorNote error={error} />
          <Field
            id="apk-file"
            label="ملف التطبيق (APK)"
            hint="يُبنى تلقائيًا في GitHub عند تغيّر الكود الأصلي (صفحة الإصدارات)"
          >
            <input
              id="apk-file"
              ref={fileRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              required
            />
          </Field>
          <div className="form-row">
            <Field id="apk-version" label="رقم الإصدار" hint="مثل 1.0.3">
              <input
                id="apk-version"
                dir="ltr"
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                maxLength={40}
                required
              />
            </Field>
            <Field id="apk-notes" label="ما الجديد (اختياري)">
              <input
                id="apk-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={1000}
              />
            </Field>
          </div>
          {progress !== null && (
            <div style={{ display: "grid", gap: 6 }}>
              <div
                className="progress"
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div style={{ width: `${progress}%` }} />
              </div>
              <span className="muted small">جارٍ الرفع… {progress}%</span>
            </div>
          )}
          <div className="form-actions">
            <button className="btn btn-primary" disabled={progress !== null || !file || !versionName.trim()}>
              <Upload size={16} strokeWidth={1.75} /> {progress !== null ? "جارٍ الرفع…" : "رفع الإصدار"}
            </button>
          </div>
        </form>
      </section>
      {org && <AndroidTestersCard organizationId={org.id} />}
    </>
  );
}
